/**
 * Loads the NOAA ENC cell manifest the converter writes to public/charts/noaa/,
 * and turns each cell into a lightweight jump entry (id, name, bounds) so the
 * real US cells appear in the chart list and tapping one flies the map to it,
 * exactly like the BSB charts. The cells themselves are rendered by MapView from
 * the per-cell GeoJSON; these entries are just for the "pick a chart, jump there"
 * list. Missing manifest (no NOAA cells added) is not an error: empty set.
 */
import { NOAA_MANIFEST_PATH, type RasterChart } from './providers';

interface CellEntry {
  id: string;
  title?: string;
  bounds: [number, number, number, number];
  scaleBand?: number;
}

function isCellEntry(c: unknown): c is CellEntry {
  if (!c || typeof c !== 'object') return false;
  const r = c as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    Array.isArray(r.bounds) &&
    r.bounds.length === 4 &&
    r.bounds.every((n) => typeof n === 'number')
  );
}

/** A scale band maps to a sensible "natural" zoom for the jump entry. */
function bandZoom(band: number | undefined): number {
  switch (band) {
    case 6: return 15;
    case 5: return 13;
    case 4: return 11;
    case 3: return 9;
    default: return 11;
  }
}

export async function loadNoaaManifest(): Promise<RasterChart[]> {
  const base = import.meta.env.BASE_URL ?? '/';
  try {
    const res = await fetch(`${base}${NOAA_MANIFEST_PATH}`);
    if (!res.ok) return [];
    const data: unknown = await res.json();
    const cells = (data as { cells?: unknown })?.cells;
    if (!Array.isArray(cells)) return [];
    return cells.filter(isCellEntry).map((c) => ({
      id: c.id,
      name: c.id + (c.scaleBand ? ` · band ${c.scaleBand}` : ''),
      bounds: c.bounds,
      path: '',
      tileMinZoom: 0,
      tileMaxZoom: 24,
      nativeZoom: bandZoom(c.scaleBand),
    }));
  } catch {
    return [];
  }
}
